import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, FileText } from 'lucide-react';
import useApi from "../../../utils/useApi";
import Swal from 'sweetalert2';

const DocumentTypeManagement = ({ handleShowHeader, handleShowSettingsHeader, user }) => {
    const { getAdminDocumentTypes, createDocumentType, updateDocumentType, deleteDocumentType } = useApi();

    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        example: '',
        description: ''
    });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        loadDocumentTypes();
    }, []);

    const loadDocumentTypes = async () => {
        try {
            setLoading(true);
            const response = await getAdminDocumentTypes();
            if (response.success) {
                setDocumentTypes(response.documentTypes || []);
            }
        } catch (error) {
            console.error('Error loading document types:', error);
            await Swal.fire({
                title: 'Error',
                text: 'Failed to load document types',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingType(null);
        setFormData({ name: '', example: '', description: '' });
        setErrors({});
        setIsModalOpen(true);
    };

    const openEditModal = (docType) => {
        setEditingType(docType);
        setFormData({
            name: docType.name || '',
            example: docType.example || '',
            description: docType.description || ''
        });
        setErrors({});
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingType(null);
        setFormData({ name: '', example: '', description: '' });
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setSaving(true);
            let response;

            if (editingType) {
                response = await updateDocumentType(editingType.id, formData);
            } else {
                response = await createDocumentType(formData);
            }

            if (response.success) {
                await Swal.fire({
                    title: 'Success!',
                    text: response.message || (editingType ? 'Document type updated successfully' : 'Document type created successfully'),
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
                closeModal();
                await loadDocumentTypes();
            }
        } catch (error) {
            await Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to save document type',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (docType) => {
        const result = await Swal.fire({
            title: 'Delete Document Type?',
            text: `Are you sure you want to delete "${docType.name}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await deleteDocumentType(docType.id);

            if (response.success) {
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'Document type has been deleted.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
                await loadDocumentTypes();
            }
        } catch (error) {
            await Swal.fire({
                title: 'Cannot Delete',
                text: error.message || 'Failed to delete document type',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        }
    };

    return (
        <div className="min-h-screen page-container z-10 relative max-w-6xl mx-auto p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Document Types
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        Manage document types available for client uploads
                    </p>
                </div>
                <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus size={20} />
                    Add Document Type
                </button>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        Loading document types...
                    </div>
                ) : documentTypes.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        <FileText size={48} className="mx-auto mb-4 opacity-50" />
                        <p>No document types found.</p>
                        <p className="text-sm mt-1">Click "Add Document Type" to create one.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                                        Example
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                {documentTypes.map((docType) => (
                                    <tr key={docType.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <FileText size={18} className="text-gray-400 mr-2" />
                                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {docType.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell">
                                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate block max-w-xs">
                                                {docType.example || '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate block max-w-md">
                                                {docType.description || '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openEditModal(docType)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(docType)}
                                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
                            onClick={closeModal}
                        />

                        {/* Modal Panel */}
                        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-md mx-auto">
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {editingType ? 'Edit Document Type' : 'Add Document Type'}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit}>
                                <div className="px-6 py-4 space-y-4">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                                                errors.name ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="e.g., W-2, 1099-INT"
                                        />
                                        {errors.name && (
                                            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                                        )}
                                    </div>

                                    {/* Example */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Example
                                        </label>
                                        <input
                                            type="text"
                                            name="example"
                                            value={formData.example}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                            placeholder="e.g., Wage and Tax Statement"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                                            placeholder="Brief description of this document type..."
                                        />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? 'Saving...' : (editingType ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentTypeManagement;
